import { computed } from '@ember/object';
import { htmlSafe } from '@ember/template';
import { next } from '@ember/runloop';
import Mixin from '@ember/object/mixin';

const VIEWPORT_PADDING = 8;
const PREFERRED_MENU_HEIGHT = 240;

// eslint-disable-next-line ember/no-new-mixins
export default Mixin.create({
  destination: null,
  destinationClass: null,
  destinationPosition: null,
  isDestinationAbove: false,

  destinationLookupToken: 0,
  isSwappingDestination: false,

  destinationElement: computed('destinationLookupToken', function () {
    let { destination } = this;

    if (!destination) {
      return null;
    }

    return document.getElementById(destination);
  }),

  _refreshDestinationElement({ allowWhileOpen = false } = {}) {
    let { destination } = this;
    let resolved = destination ? document.getElementById(destination) : null;

    if (resolved === this.destinationElement) {
      return false;
    }

    if (this.isOpen && !allowWhileOpen) {
      return false;
    }

    this.set('isSwappingDestination', true);

    // eslint-disable-next-line ember/no-runloop
    next(this, () => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      this.incrementProperty('destinationLookupToken');
      this.set('isSwappingDestination', false);

      if (this.isOpen && this._selectBoxApi) {
        this._setupDestinationOutsideListener(this._selectBoxApi);
        this._repositionDestinationDropdown(this._selectBoxApi);
        this._focusInput(this._selectBoxApi);
      }
    });

    return true;
  },

  destinationWrapperClass: computed(
    'destinationElement',
    'destinationClass',
    'isOpen',
    'isDestinationAbove',
    function () {
      if (!this.destinationElement) {
        return undefined;
      }

      return [
        this.styles.component,
        'is-destination',
        this.isOpen ? 'is-open' : null,
        this.isDestinationAbove ? 'is-destination-above' : null,
        this.destinationClass,
      ]
        .filter(Boolean)
        .join(' ');
    }
  ),

  destinationPositionStyle: computed(
    'destinationElement',
    'isOpen',
    'destinationPosition.{top,bottom,left,width,maxHeight,placement}',
    function () {
      let position = this.destinationPosition;

      if (!this.destinationElement || !this.isOpen || !position) {
        return undefined;
      }

      let { top, bottom, left, width, maxHeight, placement } = position;
      let vertical =
        placement === 'above' ? `top: auto; bottom: ${bottom}px;` : `top: ${top}px; bottom: auto;`;

      return htmlSafe(
        `position: fixed; ${vertical} left: ${left}px; width: ${width}px; max-height: ${maxHeight}px;`
      );
    }
  ),

  _notifyFocusLeave(sb, evt) {
    if (this.destinationElement) {
      if (!this._isFocusLeavePending) {
        return;
      }

      this._isFocusLeavePending = false;
    }

    this.onFocusLeave?.(evt, sb);
  },

  _armFocusLeave() {
    this._isFocusLeavePending = true;
  },

  _handleDestinationFocusLeave(sb, evt) {
    if (!this.destinationElement || !this.isOpen) {
      return;
    }

    let { relatedTarget } = evt;

    if (!relatedTarget) {
      this._focusInput(sb);
      return;
    }

    if (this._isInsideDestinationOrTrigger(relatedTarget, sb)) {
      return;
    }

    this._notifyFocusLeave(sb, evt);
    sb.close();
  },

  destinationContainerId: computed('elementId', function () {
    return `${this.elementId}-typeahead-dropdown`;
  }),

  _destinationContainer() {
    return this.destinationElement && document.getElementById(this.destinationContainerId);
  },

  _isInsideDestinationMenu(node) {
    return Boolean(node && this._destinationContainer()?.contains(node));
  },

  _isInsideDestinationOrTrigger(node, sb) {
    return Boolean(node && (this._isInsideDestinationMenu(node) || sb.element?.contains(node)));
  },

  _handleDestinationKeyDown(sb, evt) {
    if (!this.destinationElement || !sb.element) {
      return;
    }

    let forwarded = new KeyboardEvent(evt.type, {
      altKey: evt.altKey,
      bubbles: false,
      cancelable: evt.cancelable,
      code: evt.code,
      ctrlKey: evt.ctrlKey,
      key: evt.key,
      metaKey: evt.metaKey,
      shiftKey: evt.shiftKey,
    });

    Object.defineProperty(forwarded, 'keyCode', { get: () => evt.keyCode });
    Object.defineProperty(forwarded, 'which', { get: () => evt.which });

    sb.element.dispatchEvent(forwarded);

    if (forwarded.defaultPrevented) {
      evt.preventDefault();
    }
  },

  _restoreTriggerFocusIfLost(sb) {
    // eslint-disable-next-line ember/no-runloop
    next(this, () => {
      if (this.isDestroying || this.isDestroyed) {
        return;
      }

      let active = document.activeElement;

      if (!active || active === document.body) {
        sb.element?.focus();
      }
    });
  },

  _activateDestinationPortal(sb) {
    this._selectBoxApi = sb;
    this._armFocusLeave();

    if (this._refreshDestinationElement({ allowWhileOpen: true })) {
      return;
    }

    this._setupDestinationOutsideListener(sb);
    this._repositionDestinationDropdown(sb);
  },

  _deactivateDestinationPortal() {
    this.set('destinationPosition', null);
    this.set('isDestinationAbove', false);
    this._teardownDestinationOutsideListener();
    this._teardownDestinationRepositionListeners();
  },

  _repositionDestinationDropdown(sb) {
    if (!this.destinationElement) {
      return;
    }

    this._updateDestinationPosition(sb);

    // eslint-disable-next-line ember/no-runloop
    next(this, () => {
      if (this.isDestroying || this.isDestroyed || !this.isOpen) {
        return;
      }

      this._updateDestinationPosition(sb);
      this._setupDestinationRepositionListeners(sb);
    });
  },

  _updateDestinationPosition(sb) {
    let trigger = this.element?.querySelector('.typeahead-trigger-container') || sb.element;

    if (!trigger) {
      return;
    }

    let rect = trigger.getBoundingClientRect();
    let spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_PADDING;
    let spaceAbove = rect.top - VIEWPORT_PADDING;
    let openAbove = spaceBelow < PREFERRED_MENU_HEIGHT && spaceAbove > spaceBelow;

    this.set('isDestinationAbove', openAbove);
    this.set(
      'destinationPosition',
      openAbove
        ? {
            placement: 'above',
            top: null,
            bottom: window.innerHeight - rect.top,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(0, spaceAbove),
          }
        : {
            placement: 'below',
            top: rect.bottom,
            bottom: null,
            left: rect.left,
            width: rect.width,
            maxHeight: Math.max(0, spaceBelow),
          }
    );
  },

  _setupDestinationRepositionListeners(sb) {
    this._teardownDestinationRepositionListeners();

    this._destinationRepositionHandler = () => {
      if (this.isDestroying || this.isDestroyed || !this.isOpen) {
        return;
      }

      this._updateDestinationPosition(sb);
    };

    window.addEventListener('resize', this._destinationRepositionHandler);
    window.addEventListener('scroll', this._destinationRepositionHandler, true);
  },

  _teardownDestinationRepositionListeners() {
    if (this._destinationRepositionHandler) {
      window.removeEventListener('resize', this._destinationRepositionHandler);
      window.removeEventListener('scroll', this._destinationRepositionHandler, true);
      this._destinationRepositionHandler = null;
    }
  },

  _setupDestinationOutsideListener(sb) {
    if (!this.destinationElement || this._isOutsideListenerPending) {
      return;
    }

    this._teardownDestinationOutsideListener();
    this._isOutsideListenerPending = true;

    // eslint-disable-next-line ember/no-runloop
    next(this, () => {
      this._isOutsideListenerPending = false;

      if (this.isDestroying || this.isDestroyed || !this.isOpen) {
        return;
      }

      this._destinationOutsideHandler = evt => {
        if (this._isInsideDestinationOrTrigger(evt.target, sb)) {
          return;
        }

        this._notifyFocusLeave(sb, evt);
        sb.close();
        this._restoreTriggerFocusIfLost(sb);
      };

      document.addEventListener('mousedown', this._destinationOutsideHandler);
    });
  },

  _teardownDestinationOutsideListener() {
    if (this._destinationOutsideHandler) {
      document.removeEventListener('mousedown', this._destinationOutsideHandler);
      this._destinationOutsideHandler = null;
    }
  },

  didReceiveAttrs() {
    this._super(...arguments);
    this._refreshDestinationElement();
  },

  didInsertElement() {
    this._super(...arguments);

    this._focusEnterHandler = () => this._armFocusLeave();
    this.element.addEventListener('focusin', this._focusEnterHandler);
  },

  willDestroyElement() {
    if (this._focusEnterHandler) {
      this.element?.removeEventListener('focusin', this._focusEnterHandler);
      this._focusEnterHandler = null;
    }

    this._deactivateDestinationPortal();
    this._super(...arguments);
  },
});
